import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api.service';
import { usePermissions } from '../context/PermissionsContext';
import type {
  LoanEffectivePolicy,
  LoanFinancialSnapshotV1,
} from './loans/loan-financial.types';
import { formatBusinessDate, formatDateTimeAr, formatMoneyAr, moneyAr } from './loans/loan-financial.format';
import { installmentStatusLabel } from './loans/loan-financial.labels';
import { LoanFinancialSummary } from './loans/LoanFinancialSummary';
import { LoanOverdueSummary } from './loans/LoanOverdueSummary';
import { LoanCollectedSummary } from './loans/LoanCollectedSummary';
import { LoanPayoffPanel } from './loans/LoanPayoffPanel';
import { LoanRestructuringPanel } from './loans/LoanRestructuringPanel';
import { LoanCreditRiskPanel } from './loans/LoanCreditRiskPanel';
import { LoanAccountingSummary } from './loans/LoanAccountingSummary';
import { LoanReconciliationPanel } from './loans/LoanReconciliationPanel';
import { LoanJournalTable } from './loans/LoanJournalTable';
import { LoanTimeline } from './loans/LoanTimeline';
import { TransferProofModal } from '../components/TransferProofModal';

const LOAN_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  AWAITING_TRANSFER: 'Pendiente transferencia',
  APPROVED: 'Aprobado',
  ACTIVE: 'Activo',
  COMPLETED: 'Finalizado',
  REJECTED: 'Rechazado',
  CANCELLED: 'Anulado',
  DEFAULTED: 'Mora',
  LEGAL: 'Legal',
  REFINANCED: 'Refinanciado',
};

type TabId = 'resumen' | 'cuotas' | 'pagos' | 'contabilidad' | 'historial';

type AppliedSlice = {
  installmentId?: string;
  installmentNumber?: number;
  principal?: number;
  interest?: number;
  penalty?: number;
  fee?: number;
  total?: number;
  status?: string;
};

type ScheduleRow = {
  key: string;
  number: number;
  dueDate?: string | Date | null;
  chargedPrincipal: unknown;
  chargedInterest: unknown;
  chargedLateFee: unknown;
  paidPrincipal: unknown;
  paidInterest: unknown;
  paidLateFee: unknown;
  outPrincipal: unknown;
  outInterest: unknown;
  outLateFee: unknown;
  outFee: unknown;
  outTotal: unknown;
  fees: number;
  status: string;
  paidTotal: number;
};

function money(value: unknown): string {
  return moneyAr(value);
}

function dateLabel(value?: string | Date | null): string {
  return formatBusinessDate(value);
}

function dateTimeLabel(value?: string | Date | null): string {
  return formatDateTimeAr(value);
}

function actorLabel(u?: { firstName?: string; lastName?: string; email?: string } | null) {
  if (!u) return '—';
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || u.email || '—';
}

function statusClass(status: string): string {
  const map: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    AWAITING_TRANSFER: 'bg-orange-100 text-orange-800',
    APPROVED: 'bg-blue-100 text-blue-800',
    ACTIVE: 'bg-green-100 text-green-800',
    COMPLETED: 'bg-gray-100 text-gray-800',
    REJECTED: 'bg-red-100 text-red-800',
    CANCELLED: 'bg-gray-100 text-gray-600',
    DEFAULTED: 'bg-red-100 text-red-800',
    LEGAL: 'bg-purple-100 text-purple-800',
    PARTIAL: 'bg-amber-100 text-amber-800',
    PAID: 'bg-green-100 text-green-800',
    OVERDUE: 'bg-red-100 text-red-800',
    VOIDED: 'bg-gray-100 text-gray-500',
    CANCELLED_BY_PAYOFF: 'bg-slate-100 text-slate-700',
    WAIVED: 'bg-fuchsia-100 text-fuchsia-800',
  };
  return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-800'}`;
}

function paymentSlices(payment: any): AppliedSlice[] {
  if (Array.isArray(payment?.allocations) && payment.allocations.length > 0) {
    return payment.allocations as AppliedSlice[];
  }
  if (Array.isArray(payment?.applied) && payment.applied.length > 0) {
    return payment.applied as AppliedSlice[];
  }
  return [];
}

export function LoanDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can, canCritical, session } = usePermissions();

  const [loan, setLoan] = useState<any | null>(null);
  const [snapshot, setSnapshot] = useState<LoanFinancialSnapshotV1 | null>(null);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [policy, setPolicy] = useState<LoanEffectivePolicy | null>(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPaymentId, setExpandedPaymentId] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('resumen');
  const [showFeeDetail, setShowFeeDetail] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [transferProofOpen, setTransferProofOpen] = useState(false);
  const [restructureProducts, setRestructureProducts] = useState<
    Array<{ id: string; name: string; interestRate?: number }>
  >([]);

  const canAccounting = can('accounting', 'read');
  const canReconcile =
    canAccounting ||
    session?.user?.role === 'AUDITOR' ||
    session?.user?.role === 'MANAGER' ||
    session?.user?.role === 'ADMIN';
  const canPayoffQuote =
    can('loans', 'write') ||
    session?.user?.role === 'ACCOUNTANT' ||
    session?.user?.role === 'MANAGER' ||
    session?.user?.role === 'ADMIN';
  const canRestructure =
    canCritical('refinance_loan') ||
    session?.user?.role === 'ADMIN' ||
    session?.user?.role === 'MANAGER';
  const canViewCreditRisk =
    can('accounting', 'read') ||
    session?.user?.role === 'ACCOUNTANT' ||
    session?.user?.role === 'AUDITOR' ||
    session?.user?.role === 'MANAGER' ||
    session?.user?.role === 'ADMIN';
  const canPostProvision =
    canCritical('post_provision') ||
    session?.user?.role === 'ACCOUNTANT' ||
    session?.user?.role === 'ADMIN';
  const canWriteOffLoan =
    canCritical('write_off_loan') || session?.user?.role === 'ADMIN' || session?.user?.role === 'MANAGER';
  const canRecoverWrittenOff =
    canCritical('recover_written_off_loan') ||
    session?.user?.role === 'ACCOUNTANT' ||
    session?.user?.role === 'ADMIN';
  const canReversePayoff = canCritical('reverse_payment') || session?.user?.role === 'ADMIN';
  const auditorMode =
    session?.user?.role === 'ADMIN' ||
    session?.user?.role === 'ACCOUNTANT' ||
    session?.user?.role === 'AUDITOR';

  const loadCore = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setSnapshotLoading(true);
    setSnapshotError(null);
    try {
      const [loanData, snapResult] = await Promise.all([
        apiService.getLoan(id),
        apiService.getLoanFinancialSnapshot(id).then(
          (s) => ({ ok: true as const, s }),
          (e: any) => ({
            ok: false as const,
            message: e?.response?.data?.message ?? e?.message ?? 'Error al cargar snapshot',
          }),
        ),
      ]);
      setLoan(loanData);
      if (snapResult.ok) {
        setSnapshot(snapResult.s);
      } else {
        setSnapshot(null);
        setSnapshotError(
          typeof snapResult.message === 'string' ? snapResult.message : 'Error al cargar snapshot',
        );
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'No se pudo cargar el crédito');
    } finally {
      setLoading(false);
      setSnapshotLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (!canRestructure) return;
    let cancelled = false;
    apiService
      .getLoanProducts({ activeOnly: true })
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : (rows as any)?.data ?? [];
        setRestructureProducts(
          list.map((p: any) => ({
            id: p.id,
            name: p.name ?? p.shortName ?? p.id,
            interestRate: p.interestRate,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setRestructureProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canRestructure]);

  // Lazy: policy + solo si tab contabilidad / permisos
  useEffect(() => {
    if (!id || !canAccounting || (tab !== 'contabilidad' && tab !== 'resumen')) return;
    if (policy) return;
    let cancelled = false;
    setPolicyLoading(true);
    apiService
      .getLoanEffectivePolicy(id)
      .then((p) => {
        if (!cancelled) setPolicy(p);
      })
      .catch(() => {
        if (!cancelled) setPolicy(null);
      })
      .finally(() => {
        if (!cancelled) setPolicyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, canAccounting, tab, policy]);

  const scheduleRows = useMemo((): ScheduleRow[] => {
    return (loan?.installments ?? []).map((i: any): ScheduleRow => {
      const fees =
        Number(i.fee ?? 0) ||
        (i.charges ?? []).reduce((s: number, c: any) => s + Number(c.amount ?? 0), 0);
      return {
        key: i.id,
        number: i.installmentNumber,
        dueDate: i.dueDate,
        chargedPrincipal: i.principal,
        chargedInterest: i.interest,
        chargedLateFee: i.penaltyAccrued,
        paidPrincipal: null,
        paidInterest: null,
        paidLateFee: null,
        outPrincipal: null,
        outInterest: null,
        outLateFee: null,
        outFee: null,
        outTotal: Math.max(0, Number(i.totalAmount ?? i.amount ?? 0) - Number(i.paidAmount ?? 0)),
        fees,
        status: i.status,
        paidTotal: Number(i.paidAmount ?? 0),
      };
    });
  }, [loan]);

  const payments = useMemo(() => {
    const list = Array.isArray(loan?.payments) ? [...loan.payments] : [];
    return list.sort((a, b) => {
      const da = new Date(a.paymentDate ?? a.createdAt ?? 0).getTime();
      const db = new Date(b.paymentDate ?? b.createdAt ?? 0).getTime();
      return db - da;
    });
  }, [loan]);

  const nextInstallment = useMemo(() => {
    const open = scheduleRows
      .filter((r) => !['PAID', 'CANCELLED', 'CANCELLED_BY_PAYOFF', 'WAIVED'].includes(r.status))
      .sort((a, b) => new Date(a.dueDate ?? 0).getTime() - new Date(b.dueDate ?? 0).getTime());
    return open[0] ?? null;
  }, [scheduleRows]);

  if (loading) {
    return <div className="p-6 text-gray-500">Cargando ficha del crédito…</div>;
  }

  if (error) {
    return (
      <div className="p-6 space-y-3">
        <button onClick={() => navigate('/loans')} className="text-blue-600 hover:underline text-sm">
          ← Volver a préstamos
        </button>
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!loan) return null;

  const customerName = `${loan.customer?.firstName ?? ''} ${loan.customer?.lastName ?? ''}`.trim() || '—';
  const grantedAt = loan.disbursementDate ?? loan.createdAt;
  const transferOcr = loan.transferProofOcr as Record<string, unknown> | null;
  const transferMismatch = Boolean(
    loan.transferProofAmountMismatch || transferOcr?.amountMismatch,
  );

  const planRows = (loan.installments ?? []) as Array<{
    totalAmount?: number;
    amount?: number;
    installmentNumber?: number;
  }>;
  const planTotal = planRows.reduce(
    (s, i) => s + Number(i.totalAmount ?? i.amount ?? 0),
    0,
  );
  const firstInstallment = [...planRows].sort(
    (a, b) => Number(a.installmentNumber ?? 0) - Number(b.installmentNumber ?? 0),
  )[0];
  const estimatedInstallment = Number(
    firstInstallment?.totalAmount ?? firstInstallment?.amount ?? 0,
  );

  const overdueTotal = Number(snapshot?.overdue?.total ?? 0);
  const isOpenLoan = !['COMPLETED', 'CANCELLED', 'REJECTED'].includes(loan.status);
  const alDia = isOpenLoan && snapshot != null && overdueTotal <= 0;
  const conDeuda = isOpenLoan && overdueTotal > 0;
  const reconAlerts =
    snapshot?.reconciliation &&
    snapshot.reconciliation.status !== 'OK' &&
    Number(snapshot.reconciliation.findingsCount ?? 0) > 0;

  const tabs: Array<{ id: TabId; label: string; show: boolean }> = [
    { id: 'resumen', label: 'Resumen', show: true },
    { id: 'cuotas', label: 'Cuotas', show: true },
    { id: 'pagos', label: 'Pagos', show: true },
    { id: 'contabilidad', label: 'Contabilidad', show: canAccounting },
    { id: 'historial', label: 'Historial', show: true },
  ];

  const confirmReverse = async () => {
    if (reverseReason.trim().length < 5) return;
    setReversing(true);
    setActionError(null);
    try {
      await apiService.reverseEarlyPayoff(loan.id, { reason: reverseReason.trim() });
      setReverseOpen(false);
      setReverseReason('');
      await loadCore();
    } catch (e: any) {
      setActionError(e?.response?.data?.message ?? e?.message ?? 'No se pudo revertir');
    } finally {
      setReversing(false);
    }
  };

  const replaceTransferProof = async (file: File) => {
    setActionError(null);
    try {
      await apiService.replaceLoanTransferProof(loan.id, file);
      await loadCore();
    } catch (e: any) {
      setActionError(
        e?.friendlyMessage ??
          e?.response?.data?.message ??
          e?.message ??
          'No se pudo re-subir el comprobante',
      );
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={() => navigate('/loans')} className="text-blue-600 hover:underline text-sm mb-2">
            ← Volver a préstamos
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Crédito {loan.loanNumber ? `#${loan.loanNumber}` : ''}
          </h1>
          <p className="text-gray-600 mt-1">
            {loan.customerId ? (
              <Link
                to={`/customers/${loan.customerId}`}
                className="text-blue-700 hover:underline font-medium"
              >
                {customerName}
              </Link>
            ) : (
              customerName
            )}
            {loan.customer?.email ? ` · ${loan.customer.email}` : ''}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={statusClass(loan.status)}>
              {LOAN_STATUS_LABELS[loan.status] ?? loan.status}
            </span>
            {alDia ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                AL DÍA
              </span>
            ) : null}
            {conDeuda ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                CON DEUDA VENCIDA
              </span>
            ) : null}
            {loan.status === 'COMPLETED' && loan.earlyPayoffAt ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                CANCELADO (anticipado)
              </span>
            ) : null}
            {loan.status === 'REFINANCED' ||
            snapshot?.restructuring?.asSource?.status === 'CONFIRMED' ||
            loan.refinancedIntoId ||
            loan.refinancedFromId ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                REFINANCIADO
              </span>
            ) : null}
            {reconAlerts ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                CON ALERTAS CONTABLES
              </span>
            ) : null}
            <span className="text-sm text-gray-500">
              Otorgado: <strong className="text-gray-800">{dateLabel(grantedAt)}</strong>
              {loan.disbursementDate ? '' : ' (alta)'}
            </span>
            {loan.maturityDate && (
              <span className="text-sm text-gray-500">
                Vence: <strong className="text-gray-800">{dateLabel(loan.maturityDate)}</strong>
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {loan.customerId && (
            <Link
              to={`/customers/${loan.customerId}`}
              className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Ver cliente
            </Link>
          )}
          {loan.status !== 'COMPLETED' && loan.status !== 'CANCELLED' ? (
            <Link
              to="/payments"
              className="px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Ir a cobros
            </Link>
          ) : null}
          {canReversePayoff && loan.earlyPayoffAt ? (
            <button
              type="button"
              onClick={() => setReverseOpen(true)}
              className="px-3 py-2 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50"
            >
              Más acciones · Revertir payoff
            </button>
          ) : null}
        </div>
      </div>

      {/* Datos operativos existentes — no eliminar */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <InfoCard label="Monto otorgado" value={`$${money(loan.amount)}`} />
        <InfoCard
          label="Tasa / plazo"
          value={`${(Number(loan.interestRate) * 100).toFixed(1)}% · ${loan.termMonths} m`}
        />
        <InfoCard label="Producto" value={loan.product?.name ?? '—'} />
        <InfoCard
          label="Canal"
          value={
            loan.channelType === 'PUBLIC'
              ? `${loan.publicEntity?.code ?? 'Público'}${loan.employeeFileNumber ? ` · ${loan.employeeFileNumber}` : ''}`
              : 'Privado'
          }
        />
        <InfoCard label="Sucursal" value={loan.branch?.name ?? loan.branch?.code ?? '—'} />
        <InfoCard
          label="Cuota del plan"
          value={`$${money(estimatedInstallment)}`}
          hint="1ª cuota del cronograma original"
        />
        <InfoCard label="Vendedor / otorgó" value={actorLabel(loan.seller)} />
        <InfoCard
          label="Desembolsó"
          value={actorLabel(loan.disbursements?.[0]?.disbursedBy)}
          hint={dateTimeLabel(loan.disbursements?.[0]?.disbursedAt)}
        />
        <InfoCard
          label="Creó solicitud"
          value={actorLabel(loan.sourceApplication?.createdBy)}
          hint={dateTimeLabel(loan.sourceApplication?.createdAt)}
        />
        <InfoCard
          label="Revisó solicitud"
          value={actorLabel(loan.sourceApplication?.reviewedBy)}
          hint={dateTimeLabel(loan.sourceApplication?.reviewedAt)}
        />
        <InfoCard
          label="Total del plan"
          value={`$${money(planTotal)}`}
          hint="Suma de todas las cuotas al otorgar"
        />
        <InfoCard
          label="Total contractual pendiente"
          value={
            snapshot
              ? formatMoneyAr(snapshot.contractual.total)
              : snapshotError
                ? '—'
                : '…'
          }
          hint="Capital + intereses + cargos + punitorios del plan (no es saldo contable ni cancelación anticipada)"
        />
      </div>

      {nextInstallment ? (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm flex flex-wrap gap-4">
          <div>
            <p className="text-xs text-slate-500">Próxima cuota</p>
            <p className="font-medium text-slate-900">
              #{nextInstallment.number} · {dateLabel(nextInstallment.dueDate)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Importe pendiente</p>
            <p className="font-medium tabular-nums">{formatMoneyAr(nextInstallment.outTotal)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Estado</p>
            <span className={statusClass(nextInstallment.status)}>
              {installmentStatusLabel(nextInstallment.status)}
            </span>
          </div>
        </div>
      ) : null}

      {(loan.transferProofOriginalName || transferOcr) && (
        <section
          className={`rounded-lg border p-4 ${
            transferMismatch ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'
          }`}
        >
          <h2 className="text-sm font-semibold text-slate-900 mb-1">Comprobante de transferencia</h2>
          <p className="text-xs text-slate-500 mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              Archivo:{' '}
              {loan.transferProofOriginalName || loan.transferProofStoragePath ? (
                <button
                  type="button"
                  className="text-blue-700 hover:underline font-medium"
                  onClick={() => setTransferProofOpen(true)}
                >
                  {loan.transferProofOriginalName ?? 'Ver comprobante'}
                </button>
              ) : (
                '—'
              )}
              {loan.transferProofUploadedAt
                ? ` · ${dateLabel(loan.transferProofUploadedAt)}`
                : ''}
            </span>
            <label className="text-blue-700 hover:underline font-medium cursor-pointer text-xs">
              Re-subir
              <input
                type="file"
                accept="application/pdf,.pdf,image/*,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void replaceTransferProof(f);
                }}
              />
            </label>
          </p>
          {actionError ? (
            <p className="text-sm text-red-700 mb-2">{actionError}</p>
          ) : null}
          {transferMismatch && (
            <p className="text-sm font-semibold text-red-700 mb-3">
              Monto del comprobante distinto al crédito: esperado ${money(loan.amount)}
              {transferOcr?.amount != null ? ` · leído $${money(transferOcr.amount)}` : ''}
              {transferOcr?.amountDelta != null
                ? ` (Δ ${Number(transferOcr.amountDelta) > 0 ? '+' : ''}$${money(transferOcr.amountDelta)})`
                : ''}
            </p>
          )}
          {transferOcr?.ocrError ? (
            <p className="text-xs text-amber-800 mb-2">OCR: {String(transferOcr.ocrError)}</p>
          ) : null}
          {transferOcr && (
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div>
                <dt className="text-slate-500">Monto leído</dt>
                <dd className={`font-medium ${transferMismatch ? 'text-red-700' : ''}`}>
                  {transferOcr.amount != null ? `$${money(transferOcr.amount)}` : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Nº operación</dt>
                <dd className="font-medium">{String(transferOcr.operationNumber ?? '—')}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Fecha / hora</dt>
                <dd className="font-medium">
                  {String(transferOcr.date ?? '—')}
                  {transferOcr.time ? ` ${transferOcr.time}` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Banco</dt>
                <dd className="font-medium">{String(transferOcr.bank ?? '—')}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Concepto</dt>
                <dd className="font-medium">{String(transferOcr.concept ?? '—')}</dd>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className="text-slate-500">Destino</dt>
                <dd className="font-medium">
                  {[transferOcr.destinationHolder, transferOcr.destinationAlias, transferOcr.destinationCbu]
                    .filter(Boolean)
                    .map(String)
                    .join(' · ') || '—'}
                </dd>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className="text-slate-500">Origen</dt>
                <dd className="font-medium">
                  {[transferOcr.originHolder, transferOcr.originAlias, transferOcr.originCbu]
                    .filter(Boolean)
                    .map(String)
                    .join(' · ') || '—'}
                </dd>
              </div>
            </dl>
          )}
        </section>
      )}

      <div className="border-b border-slate-200">
        <nav className="flex flex-wrap gap-1" aria-label="Secciones del crédito">
          {tabs
            .filter((t) => t.show)
            .map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                  tab === t.id
                    ? 'border-blue-600 text-blue-700 font-medium'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label}
              </button>
            ))}
        </nav>
      </div>

      {tab === 'resumen' && (
        <div className="space-y-4">
          <LoanFinancialSummary
            snap={snapshot}
            loading={snapshotLoading}
            error={snapshotError}
            onRetry={() => void loadCore()}
            showFeeDetail={showFeeDetail}
            onToggleFeeDetail={() => setShowFeeDetail((v) => !v)}
          />

          {snapshot ? <LoanOverdueSummary snap={snapshot} /> : null}
          {snapshot ? <LoanCollectedSummary snap={snapshot} /> : null}
          {snapshot ? (
            <LoanPayoffPanel
              loanId={loan.id}
              snap={snapshot}
              canQuote={canPayoffQuote}
              canConfirm={canPayoffQuote}
              loanStatus={loan.status}
              onRefresh={loadCore}
            />
          ) : null}
          {snapshot ? (
            <LoanRestructuringPanel
              loanId={loan.id}
              snap={snapshot}
              canRestructure={canRestructure}
              loanStatus={loan.status}
              products={restructureProducts}
              onRefresh={loadCore}
            />
          ) : null}
          {snapshot ? (
            <LoanCreditRiskPanel
              loanId={loan.id}
              snap={snapshot}
              canView={canViewCreditRisk}
              canPost={canPostProvision}
              canWriteOff={canWriteOffLoan}
              canRecover={canRecoverWrittenOff}
              onRefresh={loadCore}
            />
          ) : null}
        </div>
      )}

      {tab === 'cuotas' && (
        <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Cronograma de cuotas</h2>
            <p className="text-xs text-gray-500">
              Capital, interés, cargos y punitorios por cuota (cobrado vs pendiente).
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Vencimiento</th>
                  <th className="px-3 py-2 text-right">Capital</th>
                  <th className="px-3 py-2 text-right">Interés</th>
                  <th className="px-3 py-2 text-right">Cargos</th>
                  <th className="px-3 py-2 text-right">Punitorios</th>
                  <th className="px-3 py-2 text-right">Pendiente</th>
                  <th className="px-3 py-2 text-right">Pagado</th>
                  <th className="px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scheduleRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                      Sin cuotas generadas todavía.
                    </td>
                  </tr>
                ) : (
                  scheduleRows.map((row) => (
                    <tr key={row.key} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">#{row.number}</td>
                      <td className="px-3 py-2 text-gray-700">{dateLabel(row.dueDate)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <ComponentCell
                          charged={row.chargedPrincipal}
                          outstanding={row.outPrincipal}
                          paid={row.paidPrincipal}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <ComponentCell
                          charged={row.chargedInterest}
                          outstanding={row.outInterest}
                          paid={row.paidInterest}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                        <ComponentCell
                          charged={row.fees > 0 ? row.fees : null}
                          outstanding={row.outFee}
                          paid={null}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <ComponentCell
                          charged={row.chargedLateFee}
                          outstanding={row.outLateFee}
                          paid={row.paidLateFee}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">
                        ${money(row.outTotal)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-green-800">
                        ${money(row.paidTotal)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={statusClass(row.status)}>
                          {installmentStatusLabel(row.status)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'pagos' && (
        <section id="pagos" className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Pagos aplicados</h2>
            <p className="text-xs text-gray-500">
              Cada cobro muestra a qué fue el dinero. Los anulados permanecen visibles y no suman a
              Cobrado.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Comprobante</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Capital</th>
                  <th className="px-3 py-2 text-right">Interés</th>
                  <th className="px-3 py-2 text-right">Punitorio</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                      Aún no se registraron pagos.
                    </td>
                  </tr>
                ) : (
                  payments.map((p) => {
                    const slices = paymentSlices(p);
                    const open = expandedPaymentId === p.id;
                    const voided = Boolean(p.voidedAt) || p.status === 'VOIDED';
                    return (
                      <PaymentRows
                        key={p.id}
                        payment={p}
                        slices={slices}
                        open={open}
                        voided={voided}
                        onToggle={() => setExpandedPaymentId(open ? null : p.id)}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'contabilidad' && canAccounting && (
        <div className="space-y-4">
          <LoanAccountingSummary
            snap={snapshot}
            policy={policy}
            policyLoading={policyLoading}
            auditorMode={auditorMode}
          />
          <LoanReconciliationPanel
            loanId={loan.id}
            fastStatus={snapshot?.reconciliation?.status}
            fastFindingsCount={snapshot?.reconciliation?.findingsCount}
            canLoadFull={canReconcile}
          />
          <LoanJournalTable
            loanId={loan.id}
            legalEntityId={loan.legalEntityId ?? loan.branch?.legalEntityId}
            loanCreatedAt={loan.createdAt}
            enabled={canAccounting && tab === 'contabilidad'}
          />
        </div>
      )}

      {tab === 'historial' && <LoanTimeline loan={loan} enabled={tab === 'historial'} />}

      {reverseOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" role="dialog" aria-modal="true">
            <h3 className="text-lg font-semibold mb-2">Revertir cancelación anticipada</h3>
            <p className="text-sm text-slate-600 mb-3">
              Esta operación generará un contraasiento y restaurará el estado financiero anterior.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (obligatorio)</label>
            <textarea
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
              placeholder="Motivo de la reversa…"
            />
            {actionError ? (
              <p className="text-sm text-red-700 mb-2">{actionError}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setReverseOpen(false);
                  setReverseReason('');
                  setActionError(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={reversing || reverseReason.trim().length < 5}
                onClick={() => void confirmReverse()}
                className="px-4 py-2 bg-red-600 text-white rounded-md disabled:opacity-50"
              >
                {reversing ? 'Revirtiendo…' : 'Revertir payoff'}
              </button>
            </div>
          </div>
        </div>
      )}

      {transferProofOpen && (
        <TransferProofModal
          loanId={loan.id}
          originalName={loan.transferProofOriginalName}
          mimeType={loan.transferProofMimeType}
          onClose={() => setTransferProofOpen(false)}
        />
      )}
    </div>
  );
}

function InfoCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-sm font-medium text-gray-900 break-words">{value}</p>
      {hint ? <p className="mt-1 text-[11px] text-gray-400 leading-snug">{hint}</p> : null}
    </div>
  );
}

function ComponentCell({
  charged,
  outstanding,
  paid,
}: {
  charged: unknown;
  outstanding: unknown;
  paid: unknown;
}) {
  const hasOutstanding = outstanding != null && outstanding !== '';
  const show = hasOutstanding ? outstanding : charged;
  const paidN = Number(paid ?? 0);
  return (
    <div>
      <div className="text-gray-900">${money(show)}</div>
      {paidN > 0 && <div className="text-[11px] text-green-700">pagado ${money(paidN)}</div>}
    </div>
  );
}

function PaymentRows({
  payment,
  slices,
  open,
  voided,
  onToggle,
}: {
  payment: any;
  slices: AppliedSlice[];
  open: boolean;
  voided: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className={`hover:bg-gray-50 ${voided ? 'opacity-70' : ''}`}>
        <td className="px-3 py-2 text-gray-900">
          {dateLabel(payment.paymentDate ?? payment.createdAt)}
          {voided && payment.voidedAt ? (
            <div className="text-[11px] text-slate-500">Anulado: {dateTimeLabel(payment.voidedAt)}</div>
          ) : null}
        </td>
        <td className="px-3 py-2 text-gray-700">
          {payment.voucher?.voucherNumber ?? payment.voucherNumber ?? '—'}
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-medium">${money(payment.amount)}</td>
        <td className="px-3 py-2 text-right tabular-nums">${money(payment.principalAmount)}</td>
        <td className="px-3 py-2 text-right tabular-nums">${money(payment.interestAmount)}</td>
        <td className="px-3 py-2 text-right tabular-nums">${money(payment.penaltyAmount)}</td>
        <td className="px-3 py-2">
          <span className={statusClass(voided ? 'VOIDED' : payment.status)}>
            {voided ? 'ANULADO' : payment.status === 'COMPLETED' ? 'Aplicado' : payment.status}
          </span>
        </td>
        <td className="px-3 py-2 text-right">
          {slices.length > 0 || voided ? (
            <button type="button" onClick={onToggle} className="text-blue-700 hover:underline text-xs">
              {open ? 'Ocultar' : 'Ver detalle'}
            </button>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50">
          <td colSpan={8} className="px-4 py-3">
            {voided ? (
              <div className="mb-3 text-xs text-slate-700 space-y-1">
                <p>
                  <strong>ANULADO</strong> — monto original ${money(payment.amount)}
                </p>
                {payment.voidReason || payment.voidedReason ? (
                  <p>Motivo: {payment.voidReason ?? payment.voidedReason}</p>
                ) : null}
                {payment.voidedBy ? <p>Usuario: {actorLabel(payment.voidedBy)}</p> : null}
              </div>
            ) : null}
            {slices.length > 0 ? (
              <>
                <p className="text-xs font-medium text-slate-700 mb-2">Aplicación por cuota</p>
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pr-3 py-1">Cuota</th>
                      <th className="pr-3 py-1 text-right">Punitorio</th>
                      <th className="pr-3 py-1 text-right">Interés</th>
                      <th className="pr-3 py-1 text-right">Capital</th>
                      <th className="py-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slices.map((s, idx) => (
                      <tr key={`${s.installmentId ?? idx}`} className="border-t border-slate-200">
                        <td className="pr-3 py-1">#{s.installmentNumber ?? '—'}</td>
                        <td className="pr-3 py-1 text-right tabular-nums">${money(s.penalty)}</td>
                        <td className="pr-3 py-1 text-right tabular-nums">${money(s.interest)}</td>
                        <td className="pr-3 py-1 text-right tabular-nums">${money(s.principal)}</td>
                        <td className="py-1 text-right tabular-nums font-medium">${money(s.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}
